import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tip } from '../components/ui/tip';
import { DatePicker } from '../components/ui/date-picker';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ArrowUpTrayIcon, CheckIcon, UsersIcon, BellAlertIcon } from '@heroicons/react/24/outline';
import { toYMD } from '../utils/fechaLocal';

interface ListRow {
  providerName: string;
  brand: string;
  lastUploadedAt?: string | null;
  lastReviewedAt?: string | null;
  nextReminderAt?: string | null;
  reviewIntervalDays?: number;
  status: 'vigente' | 'a_revisar' | 'vencida' | 'sin_revisar';
}
interface UserRow { id: string; firstName?: string; lastName?: string; username?: string; email?: string; role?: string; }

const fmt = (iso?: string | null) => iso ? new Date(iso).toLocaleDateString('es-AR') : '—';
const todayPlus = (days: number) => { const d = new Date(); d.setDate(d.getDate() + days); return toYMD(d); };

const STATUS: Record<string, { label: string; cls: string }> = {
  vigente: { label: 'Vigente', cls: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30' },
  a_revisar: { label: 'A revisar', cls: 'bg-amber-500/15 text-amber-600 border-amber-500/30' },
  vencida: { label: 'Vencida', cls: 'bg-red-500/15 text-red-600 border-red-500/30' },
  sin_revisar: { label: 'Sin revisar', cls: 'bg-muted text-muted-foreground border-border' },
};

const PriceLists: React.FC = () => {
  const navigate = useNavigate();
  const [lists, setLists] = useState<ListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmRow, setConfirmRow] = useState<ListRow | null>(null);
  const [nextDate, setNextDate] = useState('');
  const [saving, setSaving] = useState(false);
  // destinatarios
  const [recipOpen, setRecipOpen] = useState(false);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [recipients, setRecipients] = useState<string[]>([]);

  const load = useCallback(() => {
    setLoading(true);
    axios.get('/api/price-lists/tracking')
      .then((r) => setLists(r.data.lists || []))
      .catch(() => toast.error('No se pudieron cargar las listas'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadRecipients = () => {
    axios.get('/api/price-lists/recipients')
      .then((r) => { setUsers(r.data.users || []); setRecipients(r.data.recipients || []); })
      .catch(() => toast.error('No se pudieron cargar los destinatarios'));
  };
  const openRecipients = () => { loadRecipients(); setRecipOpen(true); };
  const toggleRecipient = async (userId: string, on: boolean) => {
    setRecipients((prev) => on ? [...prev, userId] : prev.filter((id) => id !== userId));
    try {
      if (on) await axios.post('/api/price-lists/recipients', { userId });
      else await axios.delete(`/api/price-lists/recipients/${userId}`);
    } catch { toast.error('No se pudo guardar'); loadRecipients(); }
  };

  const openConfirm = (row: ListRow) => {
    setConfirmRow(row);
    setNextDate(todayPlus(row.reviewIntervalDays || 30));
  };
  const doConfirm = async () => {
    if (!confirmRow) return;
    setSaving(true);
    try {
      const nextReminderAt = nextDate ? new Date(nextDate + 'T09:00:00').toISOString() : null;
      await axios.post('/api/price-lists/confirm', { providerName: confirmRow.providerName, brand: confirmRow.brand, nextReminderAt });
      toast.success('Lista confirmada. Próximo recordatorio agendado.');
      setConfirmRow(null);
      load();
    } catch { toast.error('No se pudo confirmar'); }
    finally { setSaving(false); }
  };

  const userLabel = (u: UserRow) => (u.firstName || u.lastName) ? `${u.firstName || ''} ${u.lastName || ''}`.trim() : (u.username || u.email || u.id);

  return (
    <div className="space-y-4 p-1">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-semibold">Listas de precios</h1>
          <p className="text-sm text-muted-foreground">Recordatorio de revisión de precios por proveedor</p>
        </div>
        <div className="flex gap-2">
          <Tip label="Quién recibe los avisos (campanita, email, WhatsApp)"><Button variant="outline" size="sm" className="gap-1.5" onClick={openRecipients}>
            <UsersIcon className="h-4 w-4" /> Destinatarios
          </Button></Tip>
          <Tip label="Subir/actualizar una lista de precios"><Button size="sm" className="gap-1.5" onClick={() => navigate('/products')}>
            <ArrowUpTrayIcon className="h-4 w-4" /> Subir lista
          </Button></Tip>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-sm text-muted-foreground">Cargando…</div>
          ) : lists.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground text-center">
              Todavía no hay listas de precio cargadas. Subí una lista desde <button className="underline" onClick={() => navigate('/products')}>Productos y Servicios</button>.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Proveedor</TableHead>
                  <TableHead>Marca</TableHead>
                  <TableHead>Última actualización</TableHead>
                  <TableHead>Última revisión</TableHead>
                  <TableHead>Próxima revisión</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lists.map((l) => {
                  const st = STATUS[l.status] || STATUS.sin_revisar;
                  return (
                    <TableRow key={`${l.providerName}|${l.brand}`}>
                      <TableCell className="font-medium">{l.providerName}</TableCell>
                      <TableCell>{l.brand}</TableCell>
                      <TableCell>{fmt(l.lastUploadedAt)}</TableCell>
                      <TableCell>{fmt(l.lastReviewedAt)}</TableCell>
                      <TableCell>{fmt(l.nextReminderAt)}</TableCell>
                      <TableCell><span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${st.cls}`}>{st.label}</span></TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Tip label="La lista sigue igual: registrar revisión y elegir próximo recordatorio">
                          <Button variant="outline" size="sm" className="gap-1 mr-1" onClick={() => openConfirm(l)}>
                            <CheckIcon className="h-4 w-4" /> Sigue igual
                          </Button>
                        </Tip>
                        <Tip label="Subir la lista nueva de este proveedor">
                          <Button variant="ghost" size="sm" className="gap-1" onClick={() => navigate('/products')}>
                            <ArrowUpTrayIcon className="h-4 w-4" /> Actualizar
                          </Button>
                        </Tip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Confirmar "sigue igual" */}
      <Dialog open={!!confirmRow} onOpenChange={(o) => { if (!o) setConfirmRow(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Confirmar lista de {confirmRow?.brand} ({confirmRow?.providerName})</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Registramos que la lista sigue igual hoy. ¿Cuándo querés que te vuelva a recordar revisarla?</p>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Próximo recordatorio</label>
              <DatePicker value={nextDate} onChange={setNextDate} />
            </div>
            <div className="flex gap-2 text-xs">
              <button className="underline text-muted-foreground" onClick={() => setNextDate(todayPlus(30))}>+1 mes</button>
              <button className="underline text-muted-foreground" onClick={() => setNextDate(todayPlus(60))}>+2 meses</button>
              <button className="underline text-muted-foreground" onClick={() => setNextDate(todayPlus(90))}>+3 meses</button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRow(null)}>Cancelar</Button>
            <Button onClick={doConfirm} disabled={saving || !nextDate}>{saving ? 'Guardando…' : 'Confirmar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Destinatarios */}
      <Dialog open={recipOpen} onOpenChange={(o) => { if (!o) setRecipOpen(false); }}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><BellAlertIcon className="h-5 w-5" /> Destinatarios de los avisos</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Quién recibe el recordatorio (campanita + email + WhatsApp). Agregá o quitá usuarios a elección.</p>
          <div className="space-y-1 mt-2">
            {users.map((u) => {
              const on = recipients.includes(u.id);
              return (
                <label key={u.id} className={`flex items-center gap-3 rounded-md border px-3 py-2 text-sm cursor-pointer transition-colors ${on ? 'bg-primary/10 border-primary/40' : 'border-border hover:bg-muted/50'}`}>
                  <input type="checkbox" checked={on} onChange={(e) => toggleRecipient(u.id, e.target.checked)} className="accent-primary" />
                  <div className="flex-1">
                    <span className="font-medium">{userLabel(u)}</span>
                    <span className="text-xs text-muted-foreground ml-2 capitalize">{u.role}</span>
                    {!u.email && <span className="text-xs text-amber-600 ml-2">(sin email)</span>}
                  </div>
                </label>
              );
            })}
            {users.length === 0 && <p className="text-sm text-muted-foreground">No hay usuarios.</p>}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setRecipOpen(false)}>Cerrar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PriceLists;
